import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  defaultAddress,
  formatDisplayAddressPrimary,
  getAddressParams,
} from "../../../../../../../components/Address/util";
import { generateAuthHeader } from "../../../../../../../commonfunctions/authentication";
import { Address, AddressPrimary } from "../../../../../../../interfaces/interfaces";

type Props = {
  updateShippingAddressInRedux: (a: Address | null) => void;
  clearShippingAddressInRedux: () => void;
};

export default function usePrimaryAddress({
  updateShippingAddressInRedux,
  clearShippingAddressInRedux,
}: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [isPostLoading, setIsPostLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [addressErrorMessage, setAddressErrorMessage] = useState("");
  const [primaryAddresses, setPrimaryAddresses] = useState<AddressPrimary[]>([]);
  const [listNZPostAddresses, setListNZPostAddresses] = useState<any[]>([]);
  const [selectedPrimaryAddress, setSelectedPrimaryAddress] = useState<AddressPrimary | null>(null);
  const [selectedNZPostAddress, setSelectedNZPostAddress] = useState<any | null>(null);

  // Fetch NZ Post addresses
  const fetchNZPost = useCallback((line1: string) => {
    setIsPostLoading(true);
    setAddressErrorMessage("");
    fetch(
      `${
        process.env.REACT_APP_HALFBACK_ROOT_API
      }/address_check?address_line_1=${encodeURIComponent(line1)}`,
      generateAuthHeader(),
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success" && data.result.length) {
          setListNZPostAddresses(data.result);
        } else {
          setAddressErrorMessage("Invalid NZ Post address – please contact customer service.");
        }
      })
      .catch(() => {
        setAddressErrorMessage("Error checking NZ Post address.");
      })
      .finally(() => {
        setIsPostLoading(false);
      });
  }, []);

  // 1. Load primary addresses on mount
  useEffect(() => {
    let isMounted = true;

    const loadPrimaryAddresses = () => {
      axios
        .get(
          `${process.env.REACT_APP_HALFBACK_ROOT_API}/ship_to_addresses/primary`,
          generateAuthHeader(),
        )
        .then((response) => {
          if (!isMounted) return;

          if (response.status === 200) {
            const { result } = response.data;
            setIsLoading(false);
            setPrimaryAddresses(result);
            setSelectedPrimaryAddress(result[0]);
            // trigger NZ Post lookup for the first item
            fetchNZPost(formatDisplayAddressPrimary(result[0]));
          } else if (response.status === 401) {
            alert("Session expired – please log in again.");
            window.location.href = "/";
          }
        })
        .catch(() => {
          if (isMounted) {
            setIsLoading(false);
            setErrorMessage("Sorry, we can not load your primary addresses.");
          }
        });
    };

    loadPrimaryAddresses();
    return () => {
      isMounted = false;
    };
  }, [fetchNZPost]);

  // Handler: user picks a primary address
  const onAddressSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value);
    const addr = primaryAddresses[idx];
    if (!addr) return;
    clearShippingAddressInRedux();
    setErrorMessage("");
    setSelectedPrimaryAddress(addr);
    setSelectedNZPostAddress(null);
    setListNZPostAddresses([]);
    fetchNZPost(formatDisplayAddressPrimary(addr));
  };

  // Handler: user picks a NZ Post variant
  const onNZPostChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const dpid = Number(e.target.value);
    const post = listNZPostAddresses.find((a: any) => a.DPID === dpid);

    if (!post || !selectedPrimaryAddress) {
      setSelectedNZPostAddress(null);
      updateShippingAddressInRedux(null);
      return;
    }

    // build final Address object
    const params = getAddressParams(post.FullAddress, selectedPrimaryAddress.shipToName);
    const finalAddr: Address = {
      ...defaultAddress,
      ...params,
      dpid: post.DPID,
      is_primary_address: true,
      ship_code: selectedPrimaryAddress.locationCode,
    };
    updateShippingAddressInRedux(finalAddr);
    setSelectedNZPostAddress(post);
    // update selectedPrimaryAddress.postCode in UI
    if (params)
      setSelectedPrimaryAddress({
        ...selectedPrimaryAddress,
        postCode: params?.address_postcode,
      });
  };

  return {
    isLoading,
    isPostLoading,
    errorMessage: errorMessage || addressErrorMessage,
    primaryAddresses,
    listNZPostAddresses,
    selectedPrimaryAddress,
    selectedNZPostAddress,
    onAddressSelect,
    onNZPostChange,
  };
}
